import React, { useState, useEffect } from 'react';
import { 
  Package, ShoppingCart, TrendingUp, Calendar, Bell, Printer, 
  Search, Plus, Edit, Trash2, AlertTriangle, Clock, CheckCircle,
  XCircle, Phone, MessageCircle, User, LogOut, Menu, DollarSign,
  BarChart3, Archive, Settings, ChevronRight, X, Filter
} from 'lucide-react';
import { auth, db, requestNotificationPermission } from './firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, onAuthStateChanged, sendPasswordResetEmail } from 'firebase/auth';
import { collection, query, where, onSnapshot, doc, updateDoc, getDocs, orderBy, deleteDoc, addDoc, serverTimestamp } from 'firebase/firestore';


const FarmaciaSubitoPharmacy = () => {
  // Auth State
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentPharmacy, setCurrentPharmacy] = useState(null);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // App State
  const [currentView, setCurrentView] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [todayRevenue, setTodayRevenue] = useState(0);
  const [monthRevenue, setMonthRevenue] = useState(0);
  const [showNotification, setShowNotification] = useState(false);

  const [inventory, setInventory] = useState([]);
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [openingHours, setOpeningHours] = useState({
    '2026-02-18': { morning: true, afternoon: true },
    '2026-02-19': { morning: true, afternoon: true },
    '2026-02-20': { morning: false, afternoon: true },
    '2026-02-21': { morning: true, afternoon: false }
  });

  const [selectedDate, setSelectedDate] = useState(new Date());

// Check auth state
useEffect(() => {
  const unsubscribe = onAuthStateChanged(auth, async (user) => {
    if (user) {
      const q = query(collection(db, 'farmacie'), where('email', '==', user.email));
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const pharmacyData = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
        setCurrentPharmacy(pharmacyData);
        setIsLoggedIn(true);

        // Aggiorna token FCM ad ogni apertura dashboard
        requestNotificationPermission(snapshot.docs[0].id);
      } else {
        setCurrentPharmacy(null);
        setIsLoggedIn(false);
      }
    } else {
      setCurrentPharmacy(null);
      setIsLoggedIn(false);
    }
  });
  return () => unsubscribe();
}, []);

// Listen for orders in real-time
useEffect(() => {
  if (!isLoggedIn || !currentPharmacy) return;
 
  // FILTRO: Ordini SOLO per questa farmacia
  const q = query(
    collection(db, 'orders'),
    where('farmaciaId', '==', currentPharmacy.id),
    orderBy('createdAt', 'desc')
  );
 
  const unsubscribe = onSnapshot(q, (snapshot) => {
    const fetchedOrders = [];
    snapshot.forEach((doc) => {
      fetchedOrders.push({ 
        id: doc.id, 
        ...doc.data(),
        timestamp: new Date(doc.data().createdAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
      });
    });
    
    // Show notification for new orders
    if (fetchedOrders.length > orders.length) {
      const newOrders = fetchedOrders.filter(fo => 
        !orders.some(o => o.id === fo.id) && fo.status === 'preparing'
      );
      if (newOrders.length > 0) {
        playNotificationSound();
        setShowNotification(true);
        setTimeout(() => setShowNotification(false), 5000);
      }
    }
    
    setOrders(fetchedOrders);
 
    // Calculate revenue
    const completedToday = fetchedOrders.filter(o => {
      const orderDate = new Date(o.createdAt);
      const today = new Date();
      return o.status === 'delivered' && 
             orderDate.toDateString() === today.toDateString();
    });
    const todayTotal = completedToday.reduce((sum, o) => sum + (o.total || 0), 0);
    setTodayRevenue(todayTotal);
 
    const completedMonth = fetchedOrders.filter(o => {
      const orderDate = new Date(o.createdAt);
      const today = new Date();
      return o.status === 'delivered' && 
             orderDate.getMonth() === today.getMonth() &&
             orderDate.getFullYear() === today.getFullYear();
    });
    const monthTotal = completedMonth.reduce((sum, o) => sum + (o.total || 0), 0);
    setMonthRevenue(monthTotal);
  });
 
  return () => unsubscribe();
}, [isLoggedIn, currentPharmacy, orders.length]);
  // Load products from Firebase
useEffect(() => {
  if (!isLoggedIn || !currentPharmacy) return;
  
  const loadProducts = async () => {
    try {
      const q = query(
        collection(db, 'prodotti'),
        where('farmaciaId', '==', currentPharmacy.id)
      );
      const snapshot = await getDocs(q);
      const fetchedProducts = [];
      
      snapshot.forEach((doc) => {
        fetchedProducts.push({ id: doc.id, ...doc.data() });
      });
      
      setInventory(fetchedProducts);
    } catch (error) {
      console.error('Errore caricamento prodotti:', error);
    }
  };
  
  loadProducts();
}, [isLoggedIn, currentPharmacy]);

  // Play notification sound
  const playNotificationSound = () => {
    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYHGGm98OScTgwOUKXh8LhjHAU2kdXzzn0vBSd+zPLaizsIFmS57OihUBELTKXh8bllHgU1jtTx0IU2Bhp3y/LajDgIF2W56+mjTxEKTqzn8bhjHQU2jdPx0YY2Bhp3zPLajDcIF2S46+mjThEJT63p8bpkHQU2jdTx0YU1Bhp4y/LajDgIF2S56+mjUBEJTq3p8bljHQU2jdPx0YY2Bhp3y/LbjDcIF2S56+mjTxEJTq3o8bpkHQU3jdTx0YU1Bxp4y/LajDgIF2S56+mjUBEJTqzo8bpkHQU3jdTx0YU1Bhp4y/LbjDgIF2O56+mjTxEJT6zo8bljHQU3jdPx0YY2Bhp4y/LbjDcIF2S56+mjTxEJTqzo8bpkHQU3jdTx0YY1Bhp4y/LajDgIF2O56+mjTxEKTqzp8bljHQU3jdPx0YY2Bhp4y/LbjDcIF2S56+mjTxEJTqzo8bpkHQU2jdTx0YU1Bhp4y/LajDgIF2O56+mjUBEJTqzp8bljHQU3jdPx0YY2Bhp4y/LbjDcIF2S56+mjTxEJTqzo8bpkHQU3jdTx0YU1Bhp4y/LajDgIF2S56+mjTxEJTqzo8bljHQU3jdPx0YY2Bhp4y/LbjDgIF2S56+mjTxEJTqzo8bljHAU3jdPx0YY2Bhp4y/LbjDcIF2W56+mjTxEJTqzo8bljHAU3jdPx0YY2Bhp4y/LbjDcIF2S56+mjTxEJT6zo8bpkHQU3jdPx0YU2Bhp4y/LajDcIF2S56+mjTxEJTqzo8bpkHQU3jdTx0YY1Bhp4y/LajDgIF2S56+mjTxEJTqzo8bpkHQU2jdTx0YU1Bhp4y/LbjDgIF2S56+mjTxEJTqzo8bpkHQU3jdTx0YU2Bhp4y/LbjDcIF2S56+mjTxEKTqzo8bpkHQU2jdTx0YY1Bhp4y/LajDgIF2S56+mjTxEJTqzo8bpkHQU3jdPx0YY2Bhp4y/LbjDcIF2S56+mjTxEJTq');
    audio.play().catch(() => {});
  };

  // Login Handler
  const handleLogin = async () => {
  if (!loginEmail || !loginPassword) {
    setLoginError('Inserisci email e password');
    return;
  }

  setIsLoading(true);
  setLoginError('');

  try {
    const userCredential = await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
    
    // Trova farmacia collegata a questo utente
    const q = query(collection(db, 'farmacie'), where('email', '==', loginEmail));
    const snapshot = await getDocs(q);
    
    if (!snapshot.empty) {
      const pharmacyData = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
      setCurrentPharmacy(pharmacyData);
      setIsLoggedIn(true);
    } else {
      setLoginError('Nessuna farmacia associata a questo account');
      await signOut(auth);
    }
  } catch (error) {
    setLoginError('Email o password errati');
  } finally {
    setIsLoading(false);
  }
};

// Reset Password Handler
const handleForgotPassword = async () => {
  if (!resetEmail) {
    setResetMessage('❌ Inserisci la tua email');
    return;
  }
 
  try {
    await sendPasswordResetEmail(auth, resetEmail);
    setResetMessage('✅ Email inviata! Controlla la tua casella di posta.');
    setTimeout(() => {
      setShowForgotPassword(false);
      setResetEmail('');
      setResetMessage('');
    }, 3000);
  } catch (error) {
    console.error('Errore reset password:', error);
    if (error.code === 'auth/user-not-found') {
      setResetMessage('❌ Email non trovata');
    } else {
      setResetMessage('❌ Errore durante l\'invio');
    }
  }
};

  // Delete Product
const handleDeleteProduct = async (productId) => {
  if (!window.confirm('Sei sicuro di voler eliminare questo prodotto?')) return;
  
  try {
    await deleteDoc(doc(db, 'prodotti', productId));
    alert('✅ Prodotto eliminato!');
  } catch (error) {
    console.error('Errore:', error);
    alert('❌ Errore durante l\'eliminazione');
  }
};

  // Logout Handler
  const handleLogout = async () => {
    try {
      await signOut(auth);
      setIsLoggedIn(false);
      setCurrentPharmacy(null);
      setCurrentView('orders');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Modale Password Dimenticata
const ForgotPasswordModal = () => {
  if (!showForgotPassword) return null;
 
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Password Dimenticata?</h2>
          <button
            onClick={() => {
              setShowForgotPassword(false);
              setResetEmail('');
              setResetMessage('');
            }}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            ✕
          </button>
        </div>
 
        <p className="text-gray-600 mb-4">
          Inserisci la tua email e ti invieremo un link per reimpostare la password.
        </p>
 
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
            <input
              type="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              placeholder="farmacia@example.com"
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
            />
          </div>
 
          {resetMessage && (
            <div className={`p-3 rounded-lg text-sm font-semibold ${
              resetMessage.includes('✅') 
                ? 'bg-green-50 border border-green-200 text-green-700' 
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}>
              {resetMessage}
            </div>
          )}
 
          <button
            onClick={handleForgotPassword}
            className="w-full bg-green-600 text-white py-3 rounded-lg font-bold text-lg hover:bg-green-700 transition shadow-lg"
          >
            Invia Email di Reset
          </button>
 
          <button
            onClick={() => {
              setShowForgotPassword(false);
              setResetEmail('');
              setResetMessage('');
            }}
            className="w-full bg-gray-200 text-gray-700 py-3 rounded-lg font-bold text-lg hover:bg-gray-300 transition"
          >
            Annulla
          </button>
        </div>
      </div>
    </div>
  );
};

  // Update order status
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await updateDoc(doc(db, 'orders', orderId), {
        status: newStatus,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error updating order:', error);
      alert('Errore durante l\'aggiornamento dell\'ordine');
    }
  };

  // Print order label
  const printOrderLabel = (order) => {
    const printWindow = window.open('', '', 'width=400,height=600');
    printWindow.document.write(`
      <html>
        <head>
          <title>Etichetta Ordine #${order.id.substring(0, 8)}</title>
          <style>
            body { font-family: Arial; padding: 20px; }
            h2 { border-bottom: 2px solid #000; padding-bottom: 10px; }
            .info { margin: 10px 0; }
            .items { margin-top: 20px; }
            .item { padding: 5px 0; border-bottom: 1px solid #ddd; }
          </style>
        </head>
        <body>
          <h2>FarmaciaSubito - Ordine #${order.id.substring(0, 8)}</h2>
          <div class="info">
            <strong>Cliente:</strong> ${order.userEmail}<br>
            <strong>Indirizzo:</strong> ${order.deliveryAddress || order.address}<br>
            <strong>Data:</strong> ${new Date(order.createdAt).toLocaleString('it-IT')}<br>
            ${order.codiceFiscale ? `<strong>Codice Fiscale:</strong> ${order.codiceFiscale}<br>` : ''}
          </div>
          <div class="items">
            <h3>Prodotti:</h3>
            ${order.items.map(item => `
              <div class="item">
                ${item.name} - Qtà: ${item.quantity} - €${(item.price * item.quantity).toFixed(2)}
              </div>
            `).join('')}
          </div>
          <div style="margin-top: 20px; font-size: 18px;">
            <strong>Totale: €${((order.total || 0) - (order.mancia || 0)).toFixed(2)}</strong>
            ${order.mancia > 0 ? `<p style="color:#666; font-size:14px;">Mancia rider: €${order.mancia.toFixed(2)} (non inclusa)</p>` : ''}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

// Export CSV mensile
  const exportMonthlyCSV = () => {
    const now = new Date();
    const monthOrders = orders.filter(order => {
      const orderDate = new Date(order.createdAt);
      return orderDate.getMonth() === now.getMonth() &&
             orderDate.getFullYear() === now.getFullYear();
    });

    if (monthOrders.length === 0) {
      alert('Nessun ordine questo mese');
      return;
    }

    const headers = ['ID Ordine', 'Data', 'Cliente', 'Email', 'Codice Fiscale', 'Indirizzo', 'Prodotti', 'Subtotale', 'Consegna', 'Totale', 'Stato', 'Pagamento'];
    
    const rows = monthOrders.map(order => [
      order.id.substring(0, 8),
      new Date(order.createdAt).toLocaleDateString('it-IT'),
      order.customerName || '',
      order.userEmail || '',
      order.codiceFiscale || '',
      `${order.deliveryAddress || ''} ${order.deliveryCity || ''}`.trim(),
      order.items?.map(i => `${i.quantity}x ${i.name}`).join(' | ') || '',
      (order.subtotal || 0).toFixed(2),
      (order.deliveryFee || 0).toFixed(2),
      (order.total || 0).toFixed(2),
      order.status || '',
      order.paymentStatus || ''
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ordini-${currentPharmacy.name}-${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Toggle opening hours
  const toggleOpeningHours = (date, period) => {
    const dateStr = date.toISOString().split('T')[0];
    const current = openingHours[dateStr] || { morning: false, afternoon: false };
    setOpeningHours({
      ...openingHours,
      [dateStr]: {
        ...current,
        [period]: !current[period]
      }
    });
  };

  // Get low stock products
  const getLowStockProducts = () => {
    return inventory.filter(item => item.stock <= item.minStock);
  };

  // Filter orders
  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.userEmail?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         order.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || order.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // LOGIN VIEW
if (!isLoggedIn) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <img 
            src="/MARCHIO_FARMACIASUBITO.jpg"
            alt="FarmaciaSubito" 
            className="h-28 w-auto object-contain mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-gray-800">Portale Farmacia</h1>
          <p className="text-gray-500 mt-2">Gestisci ordini e inventario</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
            <input
              type="email"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              placeholder="farmacia@example.com"
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
            <input
              type="password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              placeholder="••••••••"
              onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
            />
          </div>

          {loginError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {loginError}
            </div>
          )}

          <button
            onClick={handleLogin}
            disabled={isLoading}
            className="w-full bg-green-600 text-white py-3 rounded-lg font-bold text-lg hover:bg-green-700 transition shadow-lg disabled:bg-gray-400"
          >
            {isLoading ? 'Caricamento...' : 'Accedi'}
          </button>
                    <div className="text-center mt-4">
            <button
              onClick={() => setShowForgotPassword(true)}
              className="text-green-600 hover:text-green-700 font-semibold text-sm hover:underline"
            >
              Password dimenticata?
            </button>
          </div>
        </div>
      </div>
      <ForgotPasswordModal />
    </div>
  );
}

  // ORDERS VIEW
  const OrdersView = () => {
    const statusCounts = {
      preparing: orders.filter(o => o.status === 'preparing').length,
      ready: orders.filter(o => o.status === 'ready').length,
      delivering: orders.filter(o => o.status === 'delivering').length,
      delivered: orders.filter(o => o.status === 'delivered').length
    };

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600">In preparazione</p>
                <p className="text-3xl font-bold text-blue-700">{statusCounts.preparing}</p>
              </div>
              <Bell className="text-blue-500" size={32} />
            </div>
          </div>
          
          <div className="bg-green-50 border border-green-200 p-4 rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Pronti</p>
                <p className="text-3xl font-bold text-green-700">{statusCounts.ready}</p>
              </div>
              <CheckCircle className="text-green-500" size={32} />
            </div>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600">In consegna</p>
                <p className="text-3xl font-bold text-purple-700">{statusCounts.delivering}</p>
              </div>
              <Package className="text-purple-500" size={32} />
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 p-4 rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Consegnati</p>
                <p className="text-3xl font-bold text-gray-700">{statusCounts.delivered}</p>
              </div>
              <CheckCircle className="text-gray-500" size={32} />
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl shadow-md">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Cerca per cliente o ID ordine..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
              />
            </div>
            
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
            >
              <option value="all">Tutti gli stati</option>
              <option value="preparing">In preparazione</option>
              <option value="ready">Pronti</option>
              <option value="delivering">In consegna</option>
              <option value="delivered">Consegnati</option>
            </select>
            <button
              onClick={exportMonthlyCSV}
              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-700 transition"
              title="Esporta ordini mese corrente"
            >
              📥 Export CSV
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {filteredOrders.length === 0 ? (
            <div className="bg-white p-12 rounded-xl shadow-md text-center">
              <ShoppingCart className="mx-auto mb-4 text-gray-400" size={64} />
              <p className="text-gray-500 text-lg">Nessun ordine trovato</p>
            </div>
          ) : (
            filteredOrders.map(order => (
              <div key={order.id} className="bg-white p-6 rounded-xl shadow-md">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold text-gray-800">Ordine #{order.id.substring(0, 8)}</h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        order.status === 'preparing' ? 'bg-blue-100 text-blue-700' :
                        order.status === 'ready' ? 'bg-green-100 text-green-700' :
                        order.status === 'delivering' ? 'bg-purple-100 text-purple-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {order.status === 'preparing' ? 'In preparazione' :
                         order.status === 'ready' ? 'Pronto' :
                         order.status === 'delivering' ? 'In consegna' :
                         'Consegnato'}
                      </span>
                    </div>
                    <div className="space-y-1 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <User size={16} />
                        <span>{order.userEmail}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Package size={16} />
                        <span>{order.address}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock size={16} />
                        <span>{new Date(order.createdAt).toLocaleString('it-IT')}</span>
                      </div>
                      {order.codiceFiscale && (
                        <div className="flex items-center gap-2 bg-blue-50 px-2 py-1 rounded-lg">
                          <span>🪪</span>
                          <span className="font-semibold text-blue-700">CF: {order.codiceFiscale}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <button
                    onClick={() => printOrderLabel(order)}
                    className="bg-gray-100 hover:bg-gray-200 p-3 rounded-lg transition"
                    title="Stampa etichetta"
                  >
                    <Printer className="text-gray-700" size={20} />
                  </button>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <h4 className="font-semibold mb-2">Prodotti:</h4>
                  {order.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center py-2 border-b border-gray-200 last:border-0">
                      <span className="text-gray-700">{item.quantity}x {item.name}</span>
                      <span className="font-semibold">€{(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-3 mt-3 border-t-2 border-gray-300">
                    <span className="font-bold text-lg">Totale:</span>
                    <span className="font-bold text-xl text-green-600">€{((order.total || 0) - (order.mancia || 0)).toFixed(2)}</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  {order.status === 'preparing' && (
                    <button
                      onClick={() => updateOrderStatus(order.id, 'ready')}
                      className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-green-700 transition"
                    >
                      Segna come Pronto
                    </button>
                  )}
                  {order.status === 'ready' && (
                    <div className="flex-1 bg-green-100 text-green-700 py-2 px-4 rounded-lg font-semibold text-center">
                      In attesa del rider
                    </div>
                  )}
                  {order.status === 'delivering' && (
                    <div className="flex-1 bg-purple-100 text-purple-700 py-2 px-4 rounded-lg font-semibold text-center">
                      Rider: {order.riderName || 'Sconosciuto'}
                    </div>
                  )}
                  {order.status === 'delivered' && (
                    <div className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg font-semibold text-center">
                      ✓ Consegnato
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  // PRODUCT MODAL
const ProductModal = () => {
  const [formData, setFormData] = useState(selectedProduct || {
    name: '',
    category: 'Farmaci',
    price: '',
    stock: '',
    minStock: '',
    description: '',
    prescription: false
  });

  if (!showProductModal) return null;

  const handleSave = async () => {
    try {
      if (selectedProduct) {
        // Modifica esistente
        await updateDoc(doc(db, 'prodotti', selectedProduct.id), {
          name: formData.name,
          category: formData.category,
          price: Number(formData.price),
          stock: Number(formData.stock),
          minStock: Number(formData.minStock),
          description: formData.description,
          prescription: formData.prescription
        });
        alert('✅ Prodotto aggiornato!');
      } else {
        // Nuovo prodotto
        await addDoc(collection(db, 'prodotti'), {
          name: formData.name,
          category: formData.category,
          price: Number(formData.price),
          stock: Number(formData.stock),
          minStock: Number(formData.minStock),
          description: formData.description,
          prescription: formData.prescription,
          disponibile: true,
          createdAt: serverTimestamp()
        });
        alert('✅ Prodotto aggiunto!');
      }
      setShowProductModal(false);
      setSelectedProduct(null);
      // Ricarica prodotti
      window.location.reload();
    } catch (error) {
      console.error('Errore:', error);
      alert('❌ Errore durante il salvataggio');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            {selectedProduct ? 'Modifica Prodotto' : 'Nuovo Prodotto'}
          </h2>
          <button
            onClick={() => { setShowProductModal(false); setSelectedProduct(null); }}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <X size={24} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-sm font-bold text-gray-700 mb-2">Nome Prodotto</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-green-500 outline-none"
              placeholder="es. Tachipirina 500mg"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Categoria</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({...formData, category: e.target.value})}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-green-500 outline-none"
            >
              <option value="Farmaci">Farmaci</option>
              <option value="Integratori">Integratori</option>
              <option value="Cosmesi">Cosmesi</option>
              <option value="Dispositivi">Dispositivi</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Prezzo (€)</label>
            <input
              type="number"
              step="0.01"
              value={formData.price}
              onChange={(e) => setFormData({...formData, price: e.target.value})}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-green-500 outline-none"
              placeholder="4.90"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Scorta Attuale</label>
            <input
              type="number"
              value={formData.stock}
              onChange={(e) => setFormData({...formData, stock: e.target.value})}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-green-500 outline-none"
              placeholder="50"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Scorta Minima</label>
            <input
              type="number"
              value={formData.minStock}
              onChange={(e) => setFormData({...formData, minStock: e.target.value})}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-green-500 outline-none"
              placeholder="20"
            />
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-bold text-gray-700 mb-2">Descrizione</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-green-500 outline-none"
              placeholder="es. 20 compresse"
            />
          </div>

          <div className="col-span-2">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.prescription}
                onChange={(e) => setFormData({...formData, prescription: e.target.checked})}
                className="w-5 h-5 text-green-600 rounded focus:ring-2 focus:ring-green-500"
              />
              <span className="font-bold text-gray-700">Ricetta Richiesta</span>
            </label>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={handleSave}
            className="flex-1 bg-green-600 text-white py-3 rounded-xl font-bold hover:bg-green-700 transition"
          >
            {selectedProduct ? 'Salva Modifiche' : 'Aggiungi Prodotto'}
          </button>
          <button
            onClick={() => { setShowProductModal(false); setSelectedProduct(null); }}
            className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-xl font-bold hover:bg-gray-300 transition"
          >
            Annulla
          </button>
        </div>
      </div>
    </div>
  );
};

  // INVENTORY VIEW
const InventoryView = () => {
  const lowStockProducts = getLowStockProducts();
  
  return (
    <div className="space-y-6">
      {/* Header con bottone Aggiungi */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">Gestione Inventario</h2>
        <button
          onClick={() => { setSelectedProduct(null); setShowProductModal(true); }}
          className="bg-green-600 text-white px-4 py-2 rounded-xl font-semibold hover:bg-green-700 transition flex items-center gap-2"
        >
          <Plus size={20} />
          Aggiungi Prodotto
        </button>
      </div>

      {lowStockProducts.length > 0 && (
        <div className="bg-red-50 border-2 border-red-300 p-4 rounded-xl">
          <div className="flex items-center gap-3 mb-3">
            <AlertTriangle className="text-red-600" size={24} />
            <h3 className="font-bold text-red-800">Attenzione: {lowStockProducts.length} prodotti in esaurimento!</h3>
          </div>
          <div className="space-y-2">
            {lowStockProducts.map(product => (
              <div key={product.id} className="flex justify-between items-center bg-white p-3 rounded-lg">
                <div>
                  <p className="font-semibold text-gray-800">{product.name}</p>
                  <p className="text-sm text-gray-500">Scorta minima: {product.minStock} - Disponibili: {product.stock}</p>
                </div>
                <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-semibold">
                  Riordina!
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-xl shadow-md">
          <p className="text-sm text-gray-600 mb-1">Prodotti totali</p>
          <p className="text-3xl font-bold text-gray-800">{inventory.length}</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-md">
          <p className="text-sm text-gray-600 mb-1">Valore inventario</p>
          <p className="text-3xl font-bold text-green-600">
            €{inventory.reduce((sum, item) => sum + (item.stock * item.price), 0).toFixed(2)}
          </p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-md">
          <p className="text-sm text-gray-600 mb-1">In esaurimento</p>
          <p className="text-3xl font-bold text-red-600">{lowStockProducts.length}</p>
        </div>
      </div>

      {inventory.length === 0 ? (
        <div className="bg-white p-12 rounded-xl shadow-md text-center">
          <Package className="mx-auto mb-4 text-gray-400" size={64} />
          <p className="text-gray-500 text-lg mb-4">Nessun prodotto in inventario</p>
          <button
            onClick={() => { setSelectedProduct(null); setShowProductModal(true); }}
            className="bg-green-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-green-700 transition"
          >
            Aggiungi il primo prodotto
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left p-4 font-semibold text-gray-700">Prodotto</th>
                  <th className="text-left p-4 font-semibold text-gray-700">Categoria</th>
                  <th className="text-right p-4 font-semibold text-gray-700">Scorta</th>
                  <th className="text-right p-4 font-semibold text-gray-700">Min</th>
                  <th className="text-right p-4 font-semibold text-gray-700">Prezzo</th>
                  <th className="text-right p-4 font-semibold text-gray-700">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {inventory.map(product => (
                  <tr key={product.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-800">{product.name}</span>
                        {product.stock <= product.minStock && (
                          <AlertTriangle className="text-red-500" size={16} />
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-gray-600">{product.category}</td>
                    <td className={`p-4 text-right font-semibold ${
                      product.stock <= product.minStock ? 'text-red-600' : 'text-gray-800'
                    }`}>
                      {product.stock}
                    </td>
                    <td className="p-4 text-right text-gray-600">{product.minStock}</td>
                    <td className="p-4 text-right font-semibold text-gray-800">€{product.price.toFixed(2)}</td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => { setSelectedProduct(product); setShowProductModal(true); }}
                          className="p-2 bg-blue-100 hover:bg-blue-200 rounded-lg transition"
                        >
                          <Edit className="text-blue-600" size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteProduct(product.id)}
                          className="p-2 bg-red-100 hover:bg-red-200 rounded-lg transition"
                        >
                          <Trash2 className="text-red-600" size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

  // STATS VIEW
  const StatsView = () => {
    const completedOrders = orders.filter(o => o.status === 'delivered');
    const avgOrderValue = completedOrders.length > 0 
      ? completedOrders.reduce((sum, o) => sum + (o.total || 0), 0) / completedOrders.length 
      : 0;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-green-500 to-green-700 text-white p-6 rounded-xl shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-green-100 text-sm">Fatturato Oggi</p>
                <p className="text-4xl font-bold">€{todayRevenue.toFixed(2)}</p>
              </div>
              <DollarSign size={48} className="text-green-300" />
            </div>
            <div className="flex items-center gap-2 text-green-100">
              <TrendingUp size={16} />
              <span className="text-sm">+12% vs ieri</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-500 to-blue-700 text-white p-6 rounded-xl shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-blue-100 text-sm">Fatturato Mese</p>
                <p className="text-4xl font-bold">€{monthRevenue.toFixed(2)}</p>
              </div>
              <BarChart3 size={48} className="text-blue-300" />
            </div>
            <div className="flex items-center gap-2 text-blue-100">
              <TrendingUp size={16} />
              <span className="text-sm">+8% vs mese scorso</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-xl shadow-md">
            <p className="text-sm text-gray-600 mb-1">Ordini totali</p>
            <p className="text-3xl font-bold text-gray-800">{orders.length}</p>
          </div>
          <div className="bg-white p-4 rounded-xl shadow-md">
            <p className="text-sm text-gray-600 mb-1">Ordini completati</p>
            <p className="text-3xl font-bold text-green-600">{completedOrders.length}</p>
          </div>
          <div className="bg-white p-4 rounded-xl shadow-md">
            <p className="text-sm text-gray-600 mb-1">Valore medio ordine</p>
            <p className="text-3xl font-bold text-blue-600">€{avgOrderValue.toFixed(2)}</p>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-md">
          <h3 className="text-xl font-bold mb-4">Prodotti più venduti</h3>
          <div className="space-y-3">
            {inventory.slice(0, 5).map((product, idx) => (
              <div key={product.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-bold text-gray-400">#{idx + 1}</span>
                  <div>
                    <p className="font-semibold text-gray-800">{product.name}</p>
                    <p className="text-sm text-gray-500">{product.category}</p>
                  </div>
                </div>
                <span className="font-bold text-green-600">€{product.price.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-md">
          <h3 className="text-xl font-bold mb-4">Ordini recenti</h3>
          <div className="space-y-2">
            {orders.slice(0, 5).map(order => (
              <div key={order.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div>
                  <p className="font-semibold text-gray-800">#{order.id.substring(0, 8)} - {order.userEmail}</p>
                  <p className="text-sm text-gray-500">{new Date(order.createdAt).toLocaleString('it-IT')}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-green-600">€{order.total?.toFixed(2)}</p>
                  <p className="text-sm text-gray-500">{order.status}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // CALENDAR VIEW
  const CalendarView = () => {
    const today = new Date();
    const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).getDay();
    
    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return (
      <div className="space-y-6">
        <div className="bg-white p-6 rounded-xl shadow-md">
          <h2 className="text-2xl font-bold mb-6">Calendario Aperture</h2>
          
          <div className="grid grid-cols-7 gap-2 mb-2">
            {['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'].map(day => (
              <div key={day} className="text-center font-semibold text-gray-600 py-2">
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-2">
            {days.map((day, index) => {
              if (!day) return <div key={index} />;
              
              const date = new Date(today.getFullYear(), today.getMonth(), day);
              const dateStr = date.toISOString().split('T')[0];
              const hours = openingHours[dateStr] || { morning: false, afternoon: false };
              const isOpen = hours.morning || hours.afternoon;
              
              return (
                <button
                  key={index}
                  onClick={() => setSelectedDate(date)}
                  className={`aspect-square rounded-lg flex flex-col items-center justify-center transition ${
                    isOpen
                      ? 'bg-green-100 border-2 border-green-500'
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <span className={`text-lg font-semibold ${isOpen ? 'text-green-800' : 'text-gray-700'}`}>
                    {day}
                  </span>
                  {isOpen && (
                    <div className="flex gap-1 mt-1">
                      {hours.morning && <div className="w-2 h-2 bg-green-600 rounded-full" />}
                      {hours.afternoon && <div className="w-2 h-2 bg-green-600 rounded-full" />}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-md">
          <h3 className="font-bold text-lg mb-4">
            Orari per {selectedDate.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}
          </h3>
          
          <div className="space-y-3">
            {[
              { id: 'morning', label: 'Mattina', time: '09:00 - 13:00' },
              { id: 'afternoon', label: 'Pomeriggio', time: '16:00 - 20:00' }
            ].map(period => {
              const dateStr = selectedDate.toISOString().split('T')[0];
              const isOpen = openingHours[dateStr]?.[period.id] || false;
              
              return (
                <button
                  key={period.id}
                  onClick={() => toggleOpeningHours(selectedDate, period.id)}
                  className={`w-full p-4 rounded-lg border-2 transition ${
                    isOpen
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-left">
                      <p className="font-semibold text-gray-800">{period.label}</p>
                      <p className="text-sm text-gray-500">{period.time}</p>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      isOpen ? 'border-green-600 bg-green-600' : 'border-gray-300'
                    }`}>
                      {isOpen && <CheckCircle className="text-white" size={16} />}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // NAVIGATION
  const Navigation = () => (
    <div className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
                <div className="text-white text-2xl font-bold">+</div>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">{currentPharmacy.name}</h1>
                <p className="text-sm text-gray-500">Portale Gestione</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <button
              onClick={() => setCurrentView('orders')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition ${
                currentView === 'orders'
                  ? 'bg-green-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ShoppingCart size={20} />
              Ordini
            </button>
            
            <button
              onClick={() => setCurrentView('inventory')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition ${
                currentView === 'inventory'
                  ? 'bg-green-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Package size={20} />
              Inventario
            </button>

            <button
              onClick={() => setCurrentView('stats')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition ${
                currentView === 'stats'
                  ? 'bg-green-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <BarChart3 size={20} />
              Statistiche
            </button>

            <button
              onClick={() => setCurrentView('calendar')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition ${
                currentView === 'calendar'
                  ? 'bg-green-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Calendar size={20} />
              Calendario
            </button>

            <button
              onClick={handleLogout}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <LogOut className="text-gray-600" size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // NOTIFICATION
  const Notification = () => {
    if (!showNotification) return null;
    
    return (
      <div className="fixed top-4 right-4 bg-green-600 text-white px-6 py-4 rounded-xl shadow-2xl z-50 flex items-center gap-3 animate-bounce">
        <Bell size={24} />
        <div>
          <p className="font-bold">Nuovo Ordine!</p>
          <p className="text-sm text-green-100">Un cliente ha effettuato un ordine</p>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-6 py-8">
        {currentView === 'orders' && <OrdersView />}
        {currentView === 'inventory' && <InventoryView />}
        {currentView === 'stats' && <StatsView />}
        {currentView === 'calendar' && <CalendarView />}
      </div>

      <Notification />
      <ProductModal />
    </div>
  );
};

export default FarmaciaSubitoPharmacy;